import React, { useEffect } from 'react';
import { useAuth, useLoginWithRedirect } from '@frontegg/react';
import { useLocation } from 'react-router-dom';

/**
 * Wrapper component that redirects unauthenticated users to Frontegg login.
 * Saves the current URL so the user is redirected back after authentication.
 */
const RequireAuth = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const loginWithRedirect = useLoginWithRedirect();
    const location = useLocation();

    // Once authenticated, clear the saved after-login URL. Frontegg has
    // already consumed it for the post-login redirect by this point — but it
    // never removes the key, so a stale value (e.g. the story page a user sat
    // on when their session expired overnight) would otherwise warp every
    // future visit back into that page.
    useEffect(() => {
        if (isAuthenticated) {
            try {
                window.localStorage.removeItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL');
            } catch {
                // storage unavailable — nothing to clean
            }
        }
    }, [isAuthenticated]);

    // Design fixture mode bypasses auth for visual QA screenshots
    if (new URLSearchParams(location.search).get('designFixture') === '1') {
        return children;
    }

    // Show loading state while Frontegg checks authentication
    if (isLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                fontSize: '16px',
                color: '#555'
            }}>
                Verifying authentication...
            </div>
        );
    }

    if (!isAuthenticated) {
        // Save the intended URL using Frontegg's built-in redirect mechanism.
        // After login, Frontegg reads this from localStorage and navigates there.
        const intendedUrl = location.pathname + location.search + location.hash;
        if (intendedUrl && intendedUrl !== '/login') {
            window.localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', intendedUrl);
        }
        // Redirect to Frontegg hosted login
        loginWithRedirect();
        return null;
    }

    return children;
};

export default RequireAuth;
